import React from 'react'
import { type IntlShape } from 'react-intl'

import { pdf } from '@react-pdf/renderer'
import * as Sentry from '@sentry/react'
import { PDFDocument } from 'pdf-lib'

import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'

import { ClientRecordsExportPDF } from './ClientRecordsExportPDF'
import { getClientRecordsPdfFilename, openPdfBlob } from './recordsPdfBlob'
import {
  getRecordsPdfRedactionExcludedPageNumbers,
  type RecordsPdfAssignmentsSession,
  type RecordsPdfSectionKey,
} from './recordsPdfSections'

export type ClientRecordsPdfExport = {
  blob: Blob
  filename: string
  /** 1-based page numbers the automatic redaction pass must skip. See recordsPdfSections.ts. */
  redactionExcludedPageNumbers: number[]
}

export const openClientRecordsPdf = ({ blob }: ClientRecordsPdfExport) => openPdfBlob(blob)

export const onExportClientRecords = async ({
  clientDetails,
  intl,
  clientSummaryPDFContent,
  notesPDFContent,
  programName,
  allSections,
  benefitsSummarySection,
  sessionsSection,
  notesSection,
  progressSection,
  assignmentsSection,
  medicationsSection,
  providerId,
  providerName,
  providerInitials,
  sessionsPDFContent,
  clientProgressPDFContent,
  clientAssignmentsPDFContent,
  medicationsPDFContent,
  exportToIndexedDB,
  isEngageDAProvider,
}: {
  clientDetails: ClientObject
  intl: IntlShape
  clientSummaryPDFContent: React.ReactNode
  notesPDFContent?: { [key: string]: React.ReactNode }
  programName?: string
  notesOnly?: boolean
  allSections?: boolean
  benefitsSummarySection?: boolean
  sessionsSection?: boolean
  notesSection?: boolean
  progressSection?: boolean
  assignmentsSection?: boolean
  medicationsSection?: boolean
  providerId?: string
  providerName?: string
  /** Initials of the provider whose records are exported; appended to the filename. */
  providerInitials?: string
  sessionsPDFContent?: { [key: string]: React.ReactNode }
  clientProgressPDFContent?:
    | {
        episodeName: string
        progressByEpisode: React.ReactNode
      }[]
    | undefined
  clientAssignmentsPDFContent?:
    | {
        episodeName: string
        assignmentsBySession: RecordsPdfAssignmentsSession[]
      }[]
    | undefined
    | null
  medicationsPDFContent?: React.ReactNode
  exportToIndexedDB?: boolean
  isEngageDAProvider?: boolean
}): Promise<ClientRecordsPdfExport> => {
  const storePdfInIndexedDB = async (blob: Blob, key: string) => {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open('pdfStorage', 1)

      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains('pdfs')) {
          db.createObjectStore('pdfs')
        }
      }

      request.onsuccess = () => {
        const db = request.result
        const tx = db.transaction('pdfs', 'readwrite')
        const store = tx.objectStore('pdfs')
        store.put(blob, key)

        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
      }

      request.onerror = () => reject(request.error)
    })
  }
  const filename = getClientRecordsPdfFilename({ clientDetails, providerInitials: providerInitials ?? '' })

  try {
    const MyDocumentSections = ClientRecordsExportPDF({
      clientDetails,
      intl,
      programName,
      allSections,
      benefitsSummarySection,
      sessionsSection,
      notesSection,
      progressSection,
      assignmentsSection,
      medicationsSection,
      providerName,
      clientSummaryPDFContent,
      sessionsPDFContent,
      notesPDFContent,
      clientProgressPDFContent,
      clientAssignmentsPDFContent,
      medicationsPDFContent,
      isEngageDAProvider,
    })

    // Each section is rendered as its own PDF before being merged, so the merge is the only place
    // that knows where a section's pages land in the combined document.
    const mergeBlobs = async (sectionBlobs: { blob: Blob; sectionKey: RecordsPdfSectionKey }[]) => {
      const mergedPdf = await PDFDocument.create()
      const sectionPageCounts: { sectionKey: RecordsPdfSectionKey; pageCount: number }[] = []

      for (const { blob, sectionKey } of sectionBlobs) {
        const arrayBuffer = await blob.arrayBuffer()
        const pdf = await PDFDocument.load(arrayBuffer)
        const pageIndices = pdf.getPageIndices()

        // Copy all pages from this blob into the merged document
        const pages = await mergedPdf.copyPages(pdf, pageIndices)
        pages.forEach((page) => mergedPdf.addPage(page))
        sectionPageCounts.push({ sectionKey, pageCount: pageIndices.length })
      }

      const mergedBytes = await mergedPdf.save()
      const redactionExcludedPageNumbers = getRecordsPdfRedactionExcludedPageNumbers(sectionPageCounts)

      return {
        blob: new Blob([mergedBytes as unknown as BlobPart], { type: 'application/pdf' }),
        redactionExcludedPageNumbers,
      }
    }

    const blobs: { blob: Blob; sectionKey: RecordsPdfSectionKey }[] = []

    for (const section of MyDocumentSections) {
      try {
        const blob = await pdf(section.content as React.ReactElement).toBlob()
        blobs.push({ blob, sectionKey: section.sectionKey })
        Sentry.addBreadcrumb({
          category: 'Records Export',
          message: `Successfully generated PDF for section: ${section.sectionName}`,
          level: 'info',
          data: {
            clientId: clientDetails?.id,
            providerId,
            section: section.sectionName,
          },
        })
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'Records Export',
          message: `Failed to generate PDF for section: ${section.sectionName}`,
          level: 'error',
          data: {
            clientId: clientDetails?.id,
            providerId,
            section: section.sectionName,
            exportError: error,
          },
        })
      }
    }

    const { blob: mergedBlob, redactionExcludedPageNumbers } = await mergeBlobs(blobs)

    if (exportToIndexedDB) {
      await storePdfInIndexedDB(mergedBlob, filename)
    }
    Sentry.captureMessage('Successfully exported Client Records PDF', {
      level: 'info',
      extra: {
        clientId: clientDetails?.id,
        providerId,
      },
    })
    return { blob: mergedBlob, filename, redactionExcludedPageNumbers }
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'RECORDS_EXPORT',
      message: 'Failed to export Client Records PDF',
      level: 'error',
      data: {
        clientId: clientDetails?.id,
        providerId,
        exportError: error,
      },
    })
    Sentry.captureException(error)
    throw error
  }
}
